import * as React from "react";
import {
  ThemeProvider,
  Theme,
  StyledEngineProvider,
} from "@mui/material/styles";
import { theme } from "common/theme/theme";
import { Box } from "@mui/material";
import introspectionResult from "__generated__/fragment_matcher_unauthenticated";
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import ActionCableLink from "graphql-ruby-client/subscriptions/ActionCableLink";
import {
  campaignEmailTemplatePath,
  formBuilderFormPath,
} from "__generated__/rails_routes";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { createConsumer } from "@rails/actioncable";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getLocale } from "helpers/dateUtils/getLocale";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { PreviewFormPage } from "formBuilderPages/PreviewFormPage";
import { CampaignEmailTemplatePage } from "campaign/campaignDetails/campaignEmailTemplate/CampaignEmailTemplatePage";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

function fetchMetaContent(name: string): string {
  const element = document && document.querySelector(`meta[name=${name}]`);
  if (element) {
    return element.getAttribute("content") || "NoTextFromMeta";
  } else {
    return "NoMetaFound";
  }
}

const headers = {
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-CSRF-Token": fetchMetaContent("csrf-token"),
  "App-Release-Version": fetchMetaContent("app-release-version"),
  "App-Code-Version": fetchMetaContent("app-code-version"),
};

const hasSubscriptionOperation = ({ query: { definitions } }) => {
  return definitions.some(
    ({ kind, operation }) =>
      kind === "OperationDefinition" && operation === "subscription",
  );
};

const httpLink = new HttpLink({
  uri: "/unauthenticated_graphql",
  headers,
});
const cable = createConsumer();

const link = new RetryLink().split(
  hasSubscriptionOperation,
  new ActionCableLink({ cable }),
  httpLink,
);

const client = new ApolloClient({
  headers,
  link: link,
  cache: new InMemoryCache({
    possibleTypes: introspectionResult.possibleTypes,
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
    query: {
      fetchPolicy: "cache-first",
    },
  },
});

export const UnauthenticatedApp: React.FC = () => {
  return (
    <Box>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <ApolloProvider client={client}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={getLocale()}
            >
              <BrowserRouter>
                <Routes>
                  <Route
                    path={formBuilderFormPath(":uid")}
                    element={<PreviewFormPage />}
                  />
                  <Route
                    path={campaignEmailTemplatePath(":campaignUid")}
                    element={<CampaignEmailTemplatePage />}
                  />
                </Routes>
              </BrowserRouter>
            </LocalizationProvider>
          </ApolloProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </Box>
  );
};
